<template>
  <header class="ground-header" :class="{ 'is-floating': floating, 'is-workbench': workbench }" @click.capture="handleHeaderControlClick">
    <slot name="navigation" />
    <UiControlGroup class="ground-status" joined>
      <!-- 状态工具共用外框，每格独立处理动作、权限和状态。 -->
      <GroundStationStatusIcon
        v-if="isStatusButtonVisible('back')"
        icon="iconfont icon-fanhui7"
        tone="neutral"
        :title="$t('返回上一页')"
        data-terminal-feedback="back"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('back') }"
        interactive
        @click="handleBack"
      />
      <GroundStationStatusIcon
        v-if="isStatusButtonVisible('refresh')"
        icon="iconfont icon-shuaxinanniu"
        tone="neutral"
        :title="$t('刷新终端数据')"
        :loading="refreshing"
        data-terminal-feedback="refresh"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('refresh') }"
        interactive
        @click="$emit('refresh')"
      />
      <GroundStationStatusIcon
        v-if="isStatusButtonVisible('mqtt')"
        :icon="mqttOnline ? 'iconfont icon-yaokong2' : 'iconfont icon-yaokong2-copy'"
        :tone="mqttOnline ? 'accent' : 'danger'"
        :title="$t(mqttOnline ? '指令服务器已连接' : '指令服务器未连接')"
        :loading="mqttReconnecting"
        data-terminal-feedback="mqtt"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('mqtt') }"
        interactive
        @click="handleMqttStatusClick"
      />
      <GroundStationStatusIcon
        v-if="isStatusButtonVisible('shop')"
        icon="iconfont icon-a-shanghu_choose2x1"
        :tone="shopStatus.tone"
        :title="$t(`${shopStatus.label}：${shopStatus.reason}`)"
        :loading="shopStatusUpdating"
        data-terminal-feedback="shop"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('shop') }"
        interactive
        @click="handleShopStatusClick"
      />
      <GroundStationStatusIcon
        v-if="isStatusButtonVisible('logs')"
        icon="el-icon-document"
        tone="neutral"
        :title="$t('查看实时日志')"
        :active="logDrawerVisible"
        data-terminal-feedback="logs"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('logs') }"
        interactive
        @click="logDrawerVisible = true"
      />
      <!-- 后台管理原有的飞行魔方语音控制在统一标题栏中常驻。 -->
      <TerminalVoiceControl
        v-if="isStatusButtonVisible('voice')"
        :plane="selectedPlane"
        data-terminal-feedback="voice"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('voice') }"
      />
      <UserAvatarMenu
        @open-admin="$emit('open-admin')"
        @open-ground="$emit('open-ground')"
        :can-open-ground="canOpenGround"
        class="terminal-control-group__custom"
        data-terminal-feedback="avatar"
        :class="{ 'terminal-control-click-flash': isHeaderControlFlashing('avatar') }"
      />
    </UiControlGroup>
    <GroundStationRealtimeLogPreview
      v-if="isStatusButtonVisible('logs')"
      class="header-log-preview"
      :log="realtimeLogPreview"
    />
    <TerminalEnvironmentSummary
      ref="environmentSummary"
      class="header-environment"
      :now="now"
      :shop="shop"
      :flashing="isHeaderControlFlashing('weather')"
      :show-time="isEnvironmentItemVisible('time')"
      :show-weather="isEnvironmentItemVisible('weather')"
      :show-device-battery="isEnvironmentItemVisible('deviceBattery')"
      @weather-change="$emit('weather-change', $event)"
    />
    <!-- 1-1 与后台管理共用同一个待处理订单抽屉。 -->
    <OperationalOrderDrawer
      v-if="!inlineOrders"
      v-model="orderDrawerVisible"
      :shop-id.sync="orderDrawerShopId"
      :allow-all-shops="$store.state.user.is_god"
    />
    <GroundStationRealtimeLog
      v-if="hasBusinessPermission"
      v-model="logDrawerVisible"
      @preview-change="realtimeLogPreview = $event"
    />
  </header>
</template>

<script>
import TerminalEnvironmentSummary from '@/components/TerminalEnvironmentSummary'
import UserAvatarMenu from '@/components/UserAvatarMenu'
import OperationalOrderDrawer from '@/components/OperationalOrderDrawer'
import GroundStationStatusIcon from '@/components/ground-station/GroundStationStatusIcon'
import GroundStationRealtimeLog from '@/components/ground-station/GroundStationRealtimeLog'
import GroundStationRealtimeLogPreview from '@/components/ground-station/GroundStationRealtimeLogPreview'
import TerminalVoiceControl from '@/components/TerminalVoiceControl'
import headbarStatusActions from '@/mixins/headbarStatusActions'
import { getOrderStatusGroups, getShopOpenState } from '@/utils/operationalStatus'
import { restoreTerminalRoute, takePreviousTerminalRoute } from '@/utils/terminalRouteHistory'
import { speakText } from '@/utils/index'

export default {
  name: 'TerminalHeader',
  mixins: [headbarStatusActions],
  components: { TerminalEnvironmentSummary, UserAvatarMenu, OperationalOrderDrawer, GroundStationStatusIcon, GroundStationRealtimeLog, GroundStationRealtimeLogPreview, TerminalVoiceControl },
  props: {
    workbench: { type: Boolean, default: false },
    canOpenGround: { type: Boolean, default: false },
    inlineOrders: { type: Boolean, default: false },
    floating: { type: Boolean, default: false },
    mqttOnline: { type: Boolean, default: false },
    shop: { type: Object, default: null },
    shops: { type: Array, default: () => [] },
    selectedPlane: { type: Object, default: null },
    refreshing: { type: Boolean, default: false }
  },
  data () {
    return {
      now: new Date(),
      timer: null,
      orderDrawerVisible: false,
      logDrawerVisible: false,
      realtimeLogPreview: null,
      orderDrawerShopId: '',
      flashingHeaderControl: '',
      headerControlFlashTimer: null
    }
  },
  computed: {
    hasBusinessPermission () {
      const user = this.$store.state.user || {}
      return user.is_god === true || (Array.isArray(user.permissions) && user.permissions.length > 0)
    },
    mqttState () { return this.mqttOnline },
    currentShop () {
      if (this.shop) return this.shop
      if (this.$store.state.user.is_god) return null
      return this.shops.find(shop => String(shop.shop_id || shop.id) === String(this.$store.state.user.shop_id)) || null
    },
    shopOpenState () {
      return getShopOpenState(this.currentShop, this.now)
    },
    statusShopId () {
      if (this.currentShop) return String(this.currentShop.shop_id || this.currentShop.id || '')
      return this.$store.state.user.is_god ? '' : String(this.$store.state.user.shop_id || '')
    },
    shopStatus () {
      if (this.currentShop) {
        const state = this.shopOpenState
        return { tone: state.isOpen ? 'accent' : 'danger', label: state.isOpen ? '商铺 营业中' : '商铺 已打烊', reason: state.reason }
      }
      const states = this.shops.map(shop => getShopOpenState(shop, this.now))
      const openCount = states.filter(state => state.isOpen).length
      if (!states.length) return { tone: 'danger', label: '商铺 未获取', reason: '未获取到商铺状态' }
      return {
        tone: openCount === states.length ? 'accent' : (openCount ? 'warning' : 'danger'),
        label: `商铺 ${openCount}/${states.length} 营业`,
        reason: `当前共有 ${openCount} 家商铺营业，${states.length - openCount} 家打烊`
      }
    },
    orderStatus () {
      return getOrderStatusGroups(this.$store.state.paidOrderList, this.statusShopId)
    },
    pendingOrderSnapshot () {
      return { loaded: this.$store.state.paidOrderListLoaded, shopId: this.statusShopId, ids: this.orderStatus.pendingList.map(order => String(order.id)) }
    },
    requestedOrderSnapshot () {
      return { loaded: this.$store.state.paidOrderListLoaded, shopId: this.statusShopId, ids: this.orderStatus.requestedList.map(order => String(order.id)) }
    }
  },
  watch: {
    statusShopId: {
      immediate: true,
      handler (shopId) {
        this.orderDrawerShopId = shopId
      }
    },
    pendingOrderSnapshot (next, previous) {
      const newOrder = next.loaded && previous.loaded && next.shopId === previous.shopId && this.orderStatus.pendingList.find(order => !previous.ids.includes(String(order.id)))
      if (newOrder) {
        speakText('有新的订单')
        this.$emit('new-order', newOrder)
      }
    },
    requestedOrderSnapshot (next, previous) {
      const refundOrder = next.loaded && previous.loaded && next.shopId === previous.shopId && this.orderStatus.requestedList.find(order => !previous.ids.includes(String(order.id)))
      if (refundOrder) {
        speakText('有订单申请退款')
        this.$emit('refund-request', refundOrder)
      }
    }
  },
  mounted () {
    this.timer = setInterval(() => { this.now = new Date() }, 1000)
  },
  beforeDestroy () {
    clearInterval(this.timer)
    clearTimeout(this.headerControlFlashTimer)
  },
  methods: {
    openOrders () {
      if (this.inlineOrders) this.$emit('open-orders')
      else this.orderDrawerVisible = true
    },
    handleBack () {
      const currentFullPath = this.$route.fullPath
      const previousFullPath = takePreviousTerminalRoute(currentFullPath)
      if (!previousFullPath) {
        speakText('没有上一页')
        return
      }

      speakText('返回')
      this.$router.replace(previousFullPath).catch(() => {
        restoreTerminalRoute(currentFullPath)
      })
    },
    refreshHeaderData () {
      const summary = this.$refs.environmentSummary
      return summary && typeof summary.refreshWeather === 'function'
        ? summary.refreshWeather()
        : Promise.resolve()
    },
    isStatusButtonVisible (key) {
      return this.hasBusinessPermission && this.$store.getters['app/getStatusBarButtonVisible'](key)
    },
    isEnvironmentItemVisible (key) {
      return this.$store.getters['app/getStatusBarButtonVisible'](key)
    },
    isHeaderControlFlashing (key) {
      return this.flashingHeaderControl === key
    },
    flashHeaderControl (key) {
      clearTimeout(this.headerControlFlashTimer)
      this.flashingHeaderControl = ''
      this.$nextTick(() => {
        this.flashingHeaderControl = key
        this.headerControlFlashTimer = setTimeout(() => {
          this.flashingHeaderControl = ''
          this.headerControlFlashTimer = null
        }, 660)
      })
    },
    handleHeaderControlClick (event) {
      const target = event.target && event.target.closest
        ? event.target.closest('[data-terminal-feedback]')
        : null
      if (!target || !this.$el.contains(target) || target.disabled) return
      this.flashHeaderControl(target.dataset.terminalFeedback)
    }
  }
}
</script>

<style lang="scss" scoped>
.ground-header { height: var(--ui-terminal-header-height); position: relative; display: flex; align-items: center; padding: 0 0 0 var(--ui-space-sm); color: var(--ui-text-primary); border-bottom: 1px solid var(--ui-border-color-subtle); background: var(--ui-bg-panel); box-sizing: border-box; }
.ground-status.terminal-control-group {
  order: 1;
  height: var(--ui-icon-control-size);
  overflow: visible;
}

.ground-status [data-terminal-feedback="refresh"] {
  --terminal-control-group-icon-size: calc(var(--ui-icon-control-icon-size) * .8);
}

.header-environment { margin-right: var(--ui-space-sm); margin-left: auto; }
.header-log-preview { max-width: 720px; margin: 0 var(--ui-space-sm); }
@media (max-width: 760px) { .ground-station-status-icon { display: none; } }

.ground-header.is-floating {
  border-bottom-color: transparent;
  background: transparent;
  pointer-events: none;
}

.ground-header.is-floating > * { pointer-events: auto; }
.ground-header.is-floating > .ground-status { pointer-events: none; }
.ground-header.is-floating .ground-status > * { pointer-events: auto; }
.ground-header.is-floating > .header-log-preview { pointer-events: none; }
.ground-header.is-floating .header-environment { margin-right: 0; }
.ground-header.is-floating .ground-station-status-icon { display: grid; }

.ground-header.is-workbench {
  --terminal-status-row-height: var(--ui-terminal-header-height);
  gap: var(--ui-space-sm);
  padding-left: 0;
  padding-right: max(0px, calc((var(--terminal-status-row-height) - var(--ui-icon-control-size)) / 2));
  background: var(--ui-surface-finish), var(--ui-bg-panel);
}
.is-workbench .ground-status { margin-left: auto; flex-shrink: 0; }
.is-workbench .header-log-preview { display: none; }
@media (min-width: 1601px) {
  .ground-header.is-workbench::after { content: ''; flex: 1; }
  .is-workbench .header-environment { order: 1; }
  .is-workbench .ground-status { order: 2; }
}
@media (max-width: 1024px) {
  .ground-header.is-workbench { gap: var(--ui-space-xs); }
  .is-workbench .ground-status { min-width: 0; flex-shrink: 1; overflow-x: auto; overflow-y: hidden; }
  .is-workbench .ground-status > * { flex-shrink: 0; }
  .is-workbench .header-environment { min-width: 0; }
}
@media (max-width: 1600px) {
  .ground-header.is-workbench { gap: var(--ui-space-xs); }
  .is-workbench .ground-status { min-width: 44px; flex-shrink: 1; overflow-x: auto; overflow-y: hidden; }
  .is-workbench .ground-status > * { flex-shrink: 0; }
  .is-workbench .header-environment { margin-left: 0; margin-right: 0; flex-shrink: 0; gap: var(--ui-space-xs); padding-left: var(--ui-space-xs); padding-right: var(--ui-space-xs); }
}
@media (max-width: 950px) {
  .is-workbench .header-environment { min-width: 130px; flex-shrink: 1; overflow: hidden; }
  .is-workbench .header-environment ::v-deep .weather-wind { display: none; }
}
@media (max-width: 700px) {
  .ground-header.is-workbench { --terminal-status-row-height: 48px; }
  .is-workbench .ground-status { margin-left: auto; }
  .is-workbench .header-environment { position: absolute; top: 4px; right: 0; max-width: calc((100% - 180px) / 2 - 10px); overflow: hidden; }
  .is-workbench .header-environment ::v-deep .weather-text { display: none; }
}
@media (max-width: 760px) {
  .is-workbench .ground-station-status-icon { display: grid; }
}
</style>
